import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { TrendingUp, TrendingDown, DollarSign, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../hooks/useFormatters';

// Revolut-style gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
  40%, 43% { transform: translate3d(0,-30px,0); }
  70% { transform: translate3d(0,-15px,0); }
  90% { transform: translate3d(0,-4px,0); }
`;

// Main container with Revolut-style design
const StyledFinancialCard = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  background: linear-gradient(135deg, 
    #667eea 0%, 
    #764ba2 25%, 
    #f093fb 50%, 
    #f5576c 75%, 
    #4facfe 100%
  );
  background-size: 400% 400%;
  animation: ${gradientShift} 8s ease infinite;
  padding: 2px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset,
    0 0 80px rgba(102, 126, 234, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    animation: ${shimmer} 3s infinite;
    z-index: 1;
  }

  @media ${({ theme }) => theme.media.mobile} {
    margin: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.sm};
    border-radius: 20px;
  }
`;

const StyledCardInner = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 22px;
  padding: ${({ theme }) => theme.spacing.xl};
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media ${({ theme }) => theme.media.mobile} {
    padding: ${({ theme }) => theme.spacing.lg};
    border-radius: 18px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  @media ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
    align-items: stretch;
  }
`;

const StyledTitle = styled.h2`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media ${({ theme }) => theme.media.mobile} {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    justify-content: center;
  }
`;

const StyledBalanceSection = styled(motion.div)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
`;

const StyledBalanceLabel = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const StyledBalance = styled(motion.div)<{ $isVisible: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  
  ${({ $isVisible }) => !$isVisible && `
    filter: blur(8px);
    user-select: none;
  `}
  
  @media ${({ theme }) => theme.media.mobile} {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const StyledVisibilityToggle = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const StyledStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media ${({ theme }) => theme.media.mobile} {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const StyledStatCard = styled(motion.div)<{ $variant: 'income' | 'expense' | 'net' }>`
  background: ${({ $variant, theme }) => {
    switch ($variant) {
      case 'income': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'expense': return 'linear-gradient(135deg, #ef4444, #dc2626)';
      case 'net': return 'linear-gradient(135deg, #6366f1, #4f46e5)';
      default: return theme.colors.cardBackground;
    }
  }};
  border-radius: 20px;
  padding: ${({ theme }) => theme.spacing.lg};
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transform: translate(30px, -30px);
  }
  
  @media ${({ theme }) => theme.media.mobile} {
    padding: ${({ theme }) => theme.spacing.md};
    border-radius: 16px;
  }
`;

const StyledStatIcon = styled(motion.div)`
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: white;
  
  @media ${({ theme }) => theme.media.mobile} {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }
`;

const StyledStatLabel = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const StyledStatValue = styled(motion.div)`
  color: white;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  
  @media ${({ theme }) => theme.media.mobile} {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const StyledFloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
`;

const StyledFloatingCircle = styled(motion.div)<{ $size: number; $delay: number }>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  animation: ${bounce} ${({ $delay }) => 3 + $delay}s infinite ease-in-out;
`;

interface FinancialOverviewCardProps {
  overview: {
    totalBalance: number;
    monthlyIncome: number;
    monthlyExpenses: number;
    netIncome: number;
  };
  loading?: boolean;
}

export const FinancialOverviewCard: React.FC<FinancialOverviewCardProps> = ({
  overview,
  loading = false
}) => {
  const { t } = useTranslation();
  const { formatCurrency } = useFormatters();
  const [isVisible, setIsVisible] = useState(true);
  
  // Motion values for counter animation
  const balanceMotion = useMotionValue(0);
  const incomeMotion = useMotionValue(0);
  const expenseMotion = useMotionValue(0);
  const netMotion = useMotionValue(0);

  const balanceDisplay = useTransform(balanceMotion, latest => formatCurrency(latest));
  const incomeDisplay = useTransform(incomeMotion, latest => formatCurrency(latest));
  const expenseDisplay = useTransform(expenseMotion, latest => formatCurrency(latest));
  const netDisplay = useTransform(netMotion, latest => formatCurrency(latest));

  useEffect(() => {
    if (!loading) {
      balanceMotion.set(overview.totalBalance);
      incomeMotion.set(overview.monthlyIncome);
      expenseMotion.set(overview.monthlyExpenses);
      netMotion.set(overview.netIncome);
    }
  }, [overview, loading, balanceMotion, incomeMotion, expenseMotion, netMotion]);

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.6, 
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  if (loading) {
    return (
      <StyledFinancialCard
        initial="hidden"
        animate="visible"
        variants={cardVariants}
      >
        <StyledCardInner>
          <StyledBalanceSection>
            <StyledBalanceLabel>{t('common.loading')}</StyledBalanceLabel>
            <StyledBalance $isVisible={true}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                ⭐
              </motion.div>
            </StyledBalance>
          </StyledBalanceSection>
        </StyledCardInner>
      </StyledFinancialCard>
    );
  }

  return (
    <StyledFinancialCard
      initial="hidden"
      animate="visible"
      variants={cardVariants}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <StyledCardInner>
        <StyledFloatingElements>
          {[...Array(5)].map((_, i) => (
            <StyledFloatingCircle
              key={i}
              $size={Math.random() * 60 + 20}
              $delay={Math.random() * 2}
              initial={{ 
                x: Math.random() * 100 + '%', 
                y: Math.random() * 100 + '%' 
              }}
            />
          ))}
        </StyledFloatingElements>

        <StyledHeader>
          <motion.div variants={itemVariants}>
            <StyledTitle>
              💰 {t('dashboard.financialOverview')}
            </StyledTitle>
          </motion.div>
        </StyledHeader>

        <motion.div variants={itemVariants}>
          <StyledBalanceSection>
            <StyledBalanceLabel>
              {t('dashboard.totalBalance')}
            </StyledBalanceLabel>
            <StyledBalance $isVisible={isVisible}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
              >
                {balanceDisplay}
              </motion.div>
              <StyledVisibilityToggle
                onClick={() => setIsVisible(!isVisible)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {isVisible ? <Eye size={20} /> : <EyeOff size={20} />}
              </StyledVisibilityToggle>
            </StyledBalance>
          </StyledBalanceSection>
        </motion.div>

        <motion.div variants={itemVariants}>
          <StyledStatsGrid>
            <StyledStatCard
              $variant="income"
              whileHover={{ scale: 1.05, rotateY: 5 }}
              transition={{ duration: 0.2 }}
            >
              <StyledStatIcon
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                <TrendingUp size={24} />
              </StyledStatIcon>
              <StyledStatLabel>{t('dashboard.monthlyIncome')}</StyledStatLabel>
              <StyledStatValue>{incomeDisplay}</StyledStatValue>
            </StyledStatCard>

            <StyledStatCard
              $variant="expense"
              whileHover={{ scale: 1.05, rotateY: -5 }}
              transition={{ duration: 0.2 }}
            >
              <StyledStatIcon
                whileHover={{ rotate: -360 }}
                transition={{ duration: 0.6 }}
              >
                <TrendingDown size={24} />
              </StyledStatIcon>
              <StyledStatLabel>{t('dashboard.monthlyExpenses')}</StyledStatLabel>
              <StyledStatValue>{expenseDisplay}</StyledStatValue>
            </StyledStatCard>

            <StyledStatCard
              $variant="net"
              whileHover={{ scale: 1.05, rotateX: 5 }}
              transition={{ duration: 0.2 }}
            >
              <StyledStatIcon
                whileHover={{ scale: 1.2 }}
                transition={{ duration: 0.3 }}
              >
                <DollarSign size={24} />
              </StyledStatIcon>
              <StyledStatLabel>{t('dashboard.netIncome')}</StyledStatLabel>
              <StyledStatValue>{netDisplay}</StyledStatValue>
            </StyledStatCard>
          </StyledStatsGrid>
        </motion.div>
      </StyledCardInner>
    </StyledFinancialCard>
  );
};
